import { ColumnProps, DataTable } from "components/Table";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Box } from "@mui/material";
import { PageContainer } from 'components/PageContainer'
import { Toolbar } from "components/Toolbar";
import { Popup } from "components/Popup";
import { toast } from 'react-toastify';
import { costManagementService } from 'services/costManagementService';
import { MainContainer } from './styles';
import { FormTableLine } from './Components/FormTable';
import { AddFirstMileForm } from './Components/AddFirstMileForm';
import { transshipPointsService } from 'services/transshipPointsService';
import { ITransshipPoint } from 'interfaces/IWaybill';
import { dateToday } from 'utils/dateToday';
import { ActiveFilters } from "components/ActiveFilters";
import { CustomPagination } from "components/CustomPagination";
import dayjs from "dayjs";
import saveAs from "file-saver";
import PlanilhaIcon from "assets/planilhaIcon.svg";
export interface IFirstMile {
  id: number;
  transshipPoint: {
    id: number;
    name: string;
  };
  vehicle: {
    id: number;
    driver: {
      id: number;
      name: string;
      surname: string;
    };
    licensePlate: string;
  };
  vehicleCost: number;
  costAdjustment: number;
  leaderCommission: number;
  deliveryDate: string;
}

const defaultColumns: ColumnProps[] = [
  {
    name: "Motorista",
    align: "left",
  },
  {
    name: "Data",
    align: "center",
  },
  {
    name: "Placa",
    align: "center",
  },
  {
    name: "CD",
    align: "center",
  },
  {
    name: "Comissão do Líder",
    align: "center",
  },
  {
    name: "Valor da Rota",
    align: "center",
  },
  {
    name: "Ajuste",
    align: "center",
  },
  {
    name: "Total",
    align: "center",
  },
  {
    name: "",
    align: "center",
    minWidth: "80px",
  },
  {
    name: "",
    minWidth: "40px",
  }
]

export const FirstMile = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [transshipPoints, setTransshipPoints] = useState<ITransshipPoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [addPopupVisible, setAddPopupVisible] = useState(false);
  const [originalData, setOriginalData] = useState<IFirstMile[]>([]);
  const [editedData, setEditedData] = useState<IFirstMile[]>([]);
  const [itemsToDelete, setItemsToDelete] = useState<Set<number>>(new Set());

  const [params, setParams] = useState(() => {
    const transshipPointIds = searchParams.getAll('transshipPointIds').map(id => Number(id)).filter(id => !isNaN(id));
    const teamIds = searchParams.getAll('teamIds').map(id => Number(id)).filter(id => !isNaN(id));
    const startDate = searchParams.get('startDate') || dateToday();
    const endDate = searchParams.get('endDate') || dateToday();
    const page = Number(searchParams.get('page')) || 1;
    const pageSize = Number(searchParams.get('pageSize')) || 15;
    const search = searchParams.get('search') || '';

    return {
      transshipPointIds: transshipPointIds.length > 0 ? transshipPointIds : undefined,
      teamIds: teamIds.length > 0 ? teamIds : undefined,
      startDate,
      endDate,
      page,
      pageSize,
      search
    };
  });

  const [paginationData, setPaginationData] = useState<{
    totalPages: number;
    pageIndex: number;
  }>({
    totalPages: 1,
    pageIndex: 1
  });

  const fetchData = async () => {
    try {
      setLoading(true);
      const [data, transshipPoints] = await Promise.all([
        costManagementService.getFirstMileCost(params),
        transshipPointsService.getTransshipPoints({ pageSize: 1000, inUse: 1 }),
      ]);

      setTransshipPoints(transshipPoints.results);
      setOriginalData(data.results);
      setEditedData(data.results);
      setPaginationData({
        totalPages: data.totalPages,
        pageIndex: data.pageIndex
      });
    } catch (error: any) {
      const errorMessage = error?.error || "Erro ao buscar dados no sistema";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setAddPopupVisible(true);
  };

  const handleEditField = (id: number, field: keyof IFirstMile, value: any) => {
    setEditedData(prev => prev.map(item =>
      item.id === id ? { ...item, [field]: value } : item
    ));

    // Se o item estava marcado para deleção e está sendo editado,
    // remove da lista de deleção (usuário desistiu de deletar)
    if (itemsToDelete.has(id)) {
      setItemsToDelete(prev => {
        const newSet = new Set(prev);
        newSet.delete(id);
        return newSet;
      });
    }
  };

  const handleRollback = (id: number) => {
    const original = originalData.find(item => item.id === id);
    if (!original) return;

    // Reverter edições
    setEditedData(prev => prev.map(item =>
      item.id === id ? { ...original } : item
    ));

    // Cancelar marcação de deleção
    setItemsToDelete(prev => {
      const newSet = new Set(prev);
      newSet.delete(id);
      return newSet;
    });
  };

  const handleDelete = (id: number) => {
    setItemsToDelete(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const isRowEdited = (id: number) => {
    const orig = originalData.find(item => item.id === id);
    const edit = editedData.find(item => item.id === id);
    if (!orig || !edit) return false;
    return orig.vehicleCost !== edit.vehicleCost || orig.costAdjustment !== edit.costAdjustment;
  };

  const hasChanges = (id: number) => {
    return isRowEdited(id) || itemsToDelete.has(id);
  };

  const handleSaveBatch = async () => {
    const changed = editedData.filter(item => isRowEdited(item.id));
    const toDelete = Array.from(itemsToDelete);

    if (changed.length === 0 && toDelete.length === 0) return;

    try {
      setLoading(true);

      // Processar atualizações
      if (changed.length > 0) {
        const response = await costManagementService.updateFirstMileCostBatch({
          costs: changed.map(item => ({
            id: item.id,
            transship_point_id: item.transshipPoint.id,
            vehicle_id: item.vehicle.id,
            vehicle_cost: item.vehicleCost,
            cost_adjustment: item.costAdjustment,
            leader_commission: item.leaderCommission
          }))
        });
      }

      // Processar deleções
      if (toDelete.length > 0) {
        await costManagementService.deleteFirstMileCost({ ids: toDelete });
      }

      const successMessage = [];
      if (changed.length > 0) successMessage.push(`${changed.length} item(s) atualizado(s)`);
      if (toDelete.length > 0) successMessage.push(`${toDelete.length} item(s) deletado(s)`);

      toast.success('Alterações salvas com sucesso!');
      setItemsToDelete(new Set());
      fetchData();
    } catch (error: any) {
      const errorMessage = error?.error || "Erro ao salvar alterações";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleChangeParams = (newParamsOrFn: Function | typeof params) => {
    const updatedParams = typeof newParamsOrFn === 'function' ? newParamsOrFn(params) : newParamsOrFn;
    setParams(updatedParams);

    const newSearchParams = new URLSearchParams();
    Object.entries(updatedParams).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        value.forEach(v => newSearchParams.append(key, v.toString()));
      } else if (value) {
        newSearchParams.set(key, value.toString());
      }
    });
    setSearchParams(newSearchParams);
  };

  const handlePageChange = (page: number) => {
    handleChangeParams({ ...params, page });
  };

  const handlePageSizeChange = (pageSize: number) => {
    handleChangeParams({ ...params, page: 1, pageSize });
  };

  useEffect(() => {
    fetchData();
  }, [params]);

  // Mapeador de filtros para ActiveFilters
  const filterMapper = {
    startDate: {
      label: "Data início",
      formatValue: (value: string) => {
        try {
          return dayjs(value).format('DD/MM/YYYY');
        } catch {
          return value;
        }
      },
    },
    endDate: {
      label: "Data fim",
      formatValue: (value: string) => {
        try {
          return dayjs(value).format('DD/MM/YYYY');
        } catch {
          return value;
        }
      },
      hidden: params.startDate === params.endDate,
    },
    transshipPointIds: {
      label: "Centros de Distribuição",
      formatValue: (value: number[]) => `${value.length} selecionado${value.length !== 1 ? 's' : ''}`,
      hidden: !params.transshipPointIds || params.transshipPointIds.length === 0,
    },
    teamIds: {
      label: "Times",
      formatValue: (value: number[]) => `${value.length} selecionado${value.length !== 1 ? 's' : ''}`,
      hidden: !params.teamIds || params.teamIds.length === 0,
    }
  };

  const handleRemoveFilter = (filterKey: string) => {
    const newParams = { ...params };

    if (filterKey in newParams) {
      if (filterKey === 'transshipPointIds' || filterKey === 'teamIds') {
        (newParams as any)[filterKey] = [];
      } else if (filterKey === 'startDate' || filterKey === 'endDate') {
        const today = dateToday();
        (newParams as any)['startDate'] = today;
        (newParams as any)['endDate'] = today;
      } else if (filterKey === 'search') {
        (newParams as any)[filterKey] = '';
      }

      handleChangeParams(newParams);
    }
  };

  const handleClearAllFilters = () => {
    const today = dateToday();
    const clearedParams = {
      startDate: today,
      endDate: today,
      transshipPointIds: [],
      teamIds: [],
      search: '',
      page: 1,
      pageSize: 15,
    };

    handleChangeParams(clearedParams);
  };

  const downloadFile = async () => {
    try {
      setLoading(true);
      const exportParams = {
        startDate: params.startDate,
        endDate: params.endDate,
        search: params.search,
        transshipPointIds: params.transshipPointIds,
        teamIds: params.teamIds,
      };
      const response = await costManagementService.exportCosts('first_mile', exportParams);
      const blob = new Blob([response], { type: 'application/ms-excel;charset=utf-8' });
      saveAs(blob, 'First-Mile-Custos.xlsx');
    } catch (e) {
      toast.error("Houve um erro ao tentar baixar a planilha");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Popup
        popupTitle='Adicionar First Mile'
        open={addPopupVisible}
        setOpen={setAddPopupVisible}
        content={<AddFirstMileForm
          onSuccess={() => {
            setAddPopupVisible(false);
            fetchData();
          }}
          transshipPoints={transshipPoints}
        />}
      />

      <div className="pageContent">
        <PageContainer>
          <MainContainer>
            <div className='infos'>
              <h3>First Mile</h3>
              <Toolbar
                params={params}
                paramState={handleChangeParams}
                loadingState={loading}
                placeholder="Motorista ou placa"
                alignButtonsToEnd={true}
                add={true}
                onAdd={handleAdd}
                addDisabled={loading}
                page='first-mile-costs'
                extraButtons={[
                  {
                    text: 'Exportar planilha',
                    onClick: downloadFile,
                    disableState: loading,
                    type: 'outlined',
                    icon: <img src={PlanilhaIcon} alt="Exportar planilha" />
                  },
                  {
                    text: 'Salvar alterações',
                    onClick: handleSaveBatch,
                    disableState: (editedData.filter(item => isRowEdited(item.id)).length === 0 && itemsToDelete.size === 0) || loading,
                    type: 'filled'
                  }
                ]}
              />
            </div>

            <ActiveFilters
              params={params}
              mapper={filterMapper}
              onRemoveFilter={handleRemoveFilter}
              onClearAll={handleClearAllFilters}
              title="Filtros aplicados"
              hideWhenEmpty={true}
            />

            <DataTable loading={loading} columns={defaultColumns} contentHeight="40vh" loadingType="inline">
              {editedData.map((element) => (
                <FormTableLine
                  key={element.id}
                  element={element}
                  onEditField={handleEditField}
                  onRollback={handleRollback}
                  onDelete={handleDelete}
                  isEdited={isRowEdited(element.id)}
                  isMarkedForDeletion={itemsToDelete.has(element.id)}
                  hasChanges={hasChanges(element.id)}
                />
              ))}
            </DataTable>

            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, mb: 2 }}>
              <CustomPagination
                currentPage={paginationData.pageIndex}
                totalPages={paginationData.totalPages}
                onPageChange={handlePageChange}
                pageSize={params.pageSize}
                onPageSizeChange={handlePageSizeChange}
                showPageSizeSelector={true}
              />
            </Box>

            <div className="spacer" />
          </MainContainer>
        </PageContainer>
      </div>
    </>
  )
}
